

import React, { useState, useEffect, useRef } from 'react';
import './PayrollTable.css';
import ConfirmSubmitModal from './ConfirmSubmitModal';

const PayrollTable = () => {
  const [payrollTransactions, setPayrollTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [grantList, setGrantList] = useState([]);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showEditDefaultPaymentPopup, setShowEditDefaultPaymentPopup] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [hideConfirmedRecords, setHideConfirmedRecords] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [payrollPeriod, setPayrollPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomPeriodPopup, setShowCustomPeriodPopup] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPayrollTransactions();
    fetchGrantList();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [payrollTransactions, hideConfirmedRecords, payrollPeriod, customStartDate, customEndDate]);

  const fetchPayrollTransactions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setPayrollTransactions(data);
    } catch (error) {
      console.error('Error fetching payroll transactions:', error);
    }
  };

  const fetchGrantList = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/grants/grant-list`, {
        method: 'GET',
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const grantData = await response.json();
      setGrantList(grantData);
    } catch (error) {
      console.error('Error fetching grant list:', error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/import`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });

        if (response.ok) {
          alert('Payroll transactions imported successfully');
          fetchPayrollTransactions();
        } else {
          const errorMessage = await response.text();
          alert(`Failed to import payroll transactions: ${errorMessage}`);
        }
      } catch (error) {
        console.error('Error importing payroll transactions:', error);
        alert('Error importing payroll transactions');
      }
    }
  };

  const handleEditClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowEditPopup(true);
  };

  const handleEditDefaultPaymentClick = (transaction) => {
    setSelectedTransaction(transaction);
    setShowEditDefaultPaymentPopup(true);
  };

  const handleTimeAllocationSubmit = async () => {
    const adminTime = parseFloat(selectedTransaction['Admin Time']);
    const scienceTime = parseFloat(selectedTransaction['Science Time']);
    
    if (adminTime + scienceTime !== 100) {
      alert('Admin Time (%) + Science Time (%) must equal 100%');
      return;
    }
  
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/timeAllocation`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId: selectedTransaction['Employee Id'],
          payPeriod: selectedTransaction['Pay period'],
          adminTime: adminTime,
          scienceTime: scienceTime
        }),
      });
      if (response.ok) {
        fetchPayrollTransactions();
        setShowEditPopup(false);
      } else {
        alert('Failed to update time allocation.');
      }
    } catch (error) {
      console.error('Error updating time allocation:', error);
      alert('Error updating time allocation.');
    }
  };

    // Helper function to format Default Payment for display
    const formatDefaultPayment = (defaultPayment) => {
      if (!defaultPayment || defaultPayment === '' || defaultPayment === 'N/A') {
        return '';
      }
    
      if (typeof defaultPayment === 'string') {
        try {
          defaultPayment = JSON.parse(defaultPayment);
        } catch (error) {
          console.error('Error parsing Default Payment:', error);
          return 'Invalid format';
        }
      }
    
      if (typeof defaultPayment === 'object' && defaultPayment !== null) {
        const entries = Object.entries(defaultPayment);
        if (entries.length === 0) {
          return '';
        }
        return entries
          .map(([key, value]) => `${key}: ${value}%`)
          .join(', ');
      }
    
      return 'Invalid format';
    };
  

  const handleEditDefaultPaymentSubmit = async () => {
    const defaultPayment = selectedTransaction['Default Payment'];
    
    // Ensure defaultPayment is an object
    const defaultPaymentObject = typeof defaultPayment === 'string' ? JSON.parse(defaultPayment) : defaultPayment;
    
    const defaultPaymentKeys = Object.keys(defaultPaymentObject);
    const invalidGrants = defaultPaymentKeys.filter((grant) => !grantList.includes(grant));
  
    // Log the state before making any updates
    console.log('Selected Transaction before submitting:', selectedTransaction);
    console.log('Default Payment Object:', defaultPaymentObject);
  
    // Check if there are any invalid grants in the default payment
    if (invalidGrants.length > 0) {
      const confirmOverride = window.confirm(
        `The current Default Payment contains grants (${invalidGrants.join(", ")}) that are not in the current grant list. Editing this record will override the existing items. Do you want to proceed?`
      );
  
      if (!confirmOverride) {
        return; // If user does not confirm, return without proceeding
      }
    }
  
    const totalPercentage = Object.values(defaultPaymentObject).reduce((sum, percentage) => sum + parseFloat(percentage), 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      alert('The total percentage of default payment sources must add up to 100%.');
      return;
    }
  
    try {
      // Make the PUT request to update the default payment
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/defaultPayment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          employeeId: selectedTransaction['Employee Id'],
          payPeriod: selectedTransaction['Pay period'],
          defaultPayment: defaultPaymentObject,
        }),
      });
  
      // Check the response
      if (response.ok) {
        fetchPayrollTransactions();
        setShowEditDefaultPaymentPopup(false);
      } else {
        const errorText = await response.text();
        alert(`Failed to update default payment: ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating default payment:', error);
      alert('Error updating default payment.');
    }
  };
  
  
  const handleCheckboxChange = async (transaction) => {
    const newCheckedStatus = transaction.Checked !== 'Yes';
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/check/${transaction['Pay period']}/${transaction['Employee Id']}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ Checked: newCheckedStatus }),
      });
      if (response.ok) {
        setPayrollTransactions(payrollTransactions.map(t => 
          (t['Pay period'] === transaction['Pay period'] && t['Employee Id'] === transaction['Employee Id'])
            ? { ...t, Checked: newCheckedStatus ? 'Yes' : 'No' }
            : t
        ));
      } else {
        alert('Failed to update check status.');
      }
    } catch (error) {
      console.error('Error updating check status:', error);
      alert('Error updating check status.');
    }
  };

  const handleConfirmCheckedRecords = async () => {
    setShowConfirmModal(false);

    try {
      const checkedTransactions = payrollTransactions.filter(t => t.Checked === 'Yes');

      const incompleteTransactions = checkedTransactions.filter(transaction => 
        !transaction['Admin Time'] || 
        !transaction['Science Time'] || 
        !transaction['Default Payment'] ||
        transaction['Admin Time'] === 'N/A' || 
        transaction['Science Time'] === 'N/A' || 
        transaction['Default Payment'] === 'N/A' ||
        transaction['Admin Time'] === 'None' || 
        transaction['Science Time'] === 'None' || 
        transaction['Default Payment'] === 'None' ||
        transaction['Admin Time'] === 'null' || 
        transaction['Science Time'] === 'null' || 
        transaction['Default Payment'] === 'null'
      );

      if (incompleteTransactions.length > 0) {
        alert('Please complete the Admin Time, Science Time, and Default Payment fields for all checked records.');
        return;
      }

      const checkedIds = checkedTransactions.map(t => ({
        payPeriod: t['Pay period'],
        employeeId: t['Employee Id']
      }));

      if (checkedIds.length === 0) {
        alert('No records are checked. Please check some records before confirming.');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/confirmChecked`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ checkedIds }),
      });

      if (response.ok) {
        fetchPayrollTransactions();
        alert('Checked records confirmed successfully');
      } else {
        const errorText = await response.text();
        alert(`Failed to confirm checked records: ${errorText}`);
      }
    } catch (error) {
      console.error('Error confirming checked records:', error);
      alert('Error confirming checked records.');
    }
  };

  const handleAutoFill = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/payroll/autofill`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        fetchPayrollTransactions();
        alert('AutoFill completed successfully. Empty fields for unchecked rows have been filled with data from the closest checked row for the same employee.');
      } else {
        const errorText = await response.text();
        alert(`Failed to autofill: ${errorText}`);
      }
    } catch (error) {
      console.error('Error during autofill:', error);
      alert('Error during autofill.');
    }
  };

  const handlePayrollPeriodChange = (event) => {
    const selectedPeriod = event.target.value;
    setPayrollPeriod(selectedPeriod);
    if (selectedPeriod === 'custom') {
      setShowCustomPeriodPopup(true);
    } else {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  const handleCustomPeriodSubmit = () => {
    setShowCustomPeriodPopup(false);
    setPayrollPeriod('custom');
  };

  const filterTransactions = () => {
    let filtered = payrollTransactions;

    if (hideConfirmedRecords) {
      filtered = filtered.filter(t => t.Checked !== 'Yes');
    }

    const currentDate = new Date();
    let startDate, endDate;

    switch (payrollPeriod) {
      case 'lastYear':
        startDate = new Date(currentDate.getFullYear() - 1, 0, 1);
        endDate = new Date(currentDate.getFullYear() - 1, 11, 31);
        break;
      case 'lastQuarter':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 3, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'lastMonth':
        startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
        endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0);
        break;
      case 'custom':
        startDate = new Date(customStartDate);
        endDate = new Date(customEndDate);
        break;
      default:
        if (!isNaN(parseInt(payrollPeriod))) {
          startDate = new Date(parseInt(payrollPeriod), 0, 1);
          endDate = new Date(parseInt(payrollPeriod), 11, 31);
        }
    }

    if (startDate && endDate) {
      filtered = filtered.filter(t => {
        const transactionDate = new Date(t['Pay date']);
        return transactionDate >= startDate && transactionDate <= endDate;
      });
    }

    setFilteredTransactions(filtered);
  };

  const getPayrollPeriodOptions = () => {
    const years = [...new Set(payrollTransactions.map(t => new Date(t['Pay date']).getFullYear()))];
    return [
      { value: 'all', label: 'All' },
      ...years.map(year => ({ value: year.toString(), label: year.toString() })),
      { value: 'lastYear', label: 'Last Year' },
      { value: 'lastQuarter', label: 'Last Quarter' },
      { value: 'lastMonth', label: 'Last Month' },
      { value: 'custom', label: 'Custom Period' }
    ];
  };

  return (
    <div className="payroll-table-container">
      <div className="payroll-controls">
        <select value={payrollPeriod} onChange={handlePayrollPeriodChange}>
          {getPayrollPeriodOptions().map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <button onClick={() => setHideConfirmedRecords(!hideConfirmedRecords)}>
          {hideConfirmedRecords ? 'Show Confirmed Records' : 'Hide Confirmed Records'}
        </button>
        <button onClick={() => setShowConfirmModal(true)}>Confirm Checked Records</button>
        <button onClick={handleAutoFill}>AutoFill</button>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />
        <button onClick={() => fileInputRef.current.click()}>Import Payroll Transactions</button>
      </div>
      <table className="payroll-table">
        <thead>
          <tr>
            <th>Pay period</th>
            <th>Name</th>
            <th>Employee Id</th>
            {/* <th>Description</th>
            <th>Payment</th> */}
            <th>Admin Time (%)</th>
            <th>Science Time (%)</th>
            <th>Default Payment (%)</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTransactions.map((transaction) => (
            <tr key={`${transaction['Pay period']}_${transaction['Employee Id']}`}>
              <td>{transaction['Pay period']}</td>
              <td>{transaction['Name']}</td>
              <td>{transaction['Employee Id']}</td>
              {/* <td>{transaction['Description']}</td>
              <td>{transaction['Payment']}</td> */}
              <td>{transaction['Admin Time']}</td>
              <td>{transaction['Science Time']}</td>
              <td>{formatDefaultPayment(transaction['Default Payment'])}</td>
              <td>
                <button onClick={() => handleEditClick(transaction)}>Edit Time</button>
                <button onClick={() => handleEditDefaultPaymentClick(transaction)}>Edit Default Payment</button>
                <input
                  type="checkbox"
                  checked={transaction.Checked === 'Yes'}
                  onChange={() => handleCheckboxChange(transaction)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {showEditPopup && (
        <div className="popup">
          <h3>Edit Time Allocation</h3>
          <div>
            <label>Admin Time (%)</label>
            <select
              value={selectedTransaction['Admin Time']}
              onChange={(e) => setSelectedTransaction({ ...selectedTransaction, 'Admin Time': e.target.value })}
            >
              {Array.from({ length: 101 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <div>
            <label>Science Time (%)</label>
            <select
              value={selectedTransaction['Science Time']}
              onChange={(e) => setSelectedTransaction({ ...selectedTransaction, 'Science Time': e.target.value })}
            >
              {Array.from({ length: 101 }, (_, i) => (
                <option key={i} value={i}>{i}</option>
              ))}
            </select>
          </div>
          <button onClick={handleTimeAllocationSubmit}>Save</button>
          <button onClick={() => setShowEditPopup(false)}>Cancel</button>
        </div>
      )}
      {showEditDefaultPaymentPopup && (
        <div className="popup">
          <h3>Edit Default Payment</h3>
          <p>Please fill in the percentage of each payment source. The sum of all payment source percentages should be 100%</p>
          {grantList.map((grant) => (
            <div key={grant}>
              <label>{grant}</label>
              <select
                value={selectedTransaction['Default Payment'][grant] || 0}
                onChange={(e) => {
                  const newPercentage = parseInt(e.target.value, 10) || 0;
                  setSelectedTransaction({
                    ...selectedTransaction,
                    'Default Payment': {
                      ...selectedTransaction['Default Payment'],
                      [grant]: newPercentage,
                    },
                  });
                }}
              >
                {Array.from({ length: 101 }, (_, i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
          ))}
          <button onClick={handleEditDefaultPaymentSubmit}>Save</button>
          <button onClick={() => setShowEditDefaultPaymentPopup(false)}>Cancel</button>
        </div>
      )}
      {showCustomPeriodPopup && (
        <div className="popup">
          <h3>Select Custom Period</h3>
          <div>
            <label>Start Date</label>
            <input
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
            />
          </div>
          <button onClick={handleCustomPeriodSubmit}>Apply</button>
          <button onClick={() => setShowCustomPeriodPopup(false)}>Cancel</button>
        </div>
      )}

      <ConfirmSubmitModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmCheckedRecords}
      />
    </div>
  );
};

export default PayrollTable;